
import {
  Delegation,
  IDelegationData,
  ISubclientDetail,
  ISubclientDetailData,
  IThirdcode,
  IUpdateSubclient,
} from "@/entities";
import { IGenericResponse } from "@/services";
import { ValidationObserver } from "vee-validate";
import Multiselect from "vue-multiselect";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action } from "vuex-class";
import ThirdcodeTable from "@/components/thirdcode/ThirdcodeTable.vue";
import LimitsTable from "@/components/limits/LimitsTable.vue";

@Component({
  name: "subclient-editable-detail",
  components: {
    ValidationObserver,
    Multiselect,
    ThirdcodeTable,
    LimitsTable,
  },
  props: ["detail"],
})
export default class SubclientEditableDetail extends Vue {
  @Prop() public detail!: ISubclientDetail;

  @Action("saveUpdateSubclient") saveUpdateSubclient!: (
    updateSubclient: IUpdateSubclient
  ) => Promise<IGenericResponse<ISubclientDetailData> | undefined>;

  @Action("fetchDelegationsByCountry") fetchDelegationsByCountry!: (
    idCountry: number | undefined
  ) => Promise<IDelegationData[] | undefined>;

  @Action("updateStatusSubclient") updateStatusSubclient!: (
    idSubclient: number
  ) => Promise<number | undefined>;

  public oidSolInput?: string | null = null;
  public codeSolInput?: string | null = null;
  public idSmartcashInput?: string | null = null;
  public uniqueCodeInput?: string | null = null;
  public descriptionInput?: string | null = null;
  public delegationInput?: string | null = null;
  public subclientDelegationSelected?: string | null = null;
  public optionsDelegations: string[] | undefined = [];
  public thirdCodes?: IThirdcode[] = [];

  constructor() {
    super();
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  async save(): Promise<void> {
    const updateSubclient: IUpdateSubclient = {
      id: this.detail.id!,
      oidSol: this.oidSolInput!,
      codeSol: this.codeSolInput!,
      uniqueCode: this.uniqueCodeInput!,
      description: this.descriptionInput!,
      idSmartcash: this.idSmartcashInput!,
      active: this.detail.active!,
      clientId: this.detail.client!.id!,
      delegation: this.subclientDelegationSelected!.split(" -")[0],
      thirdcodes: this.detail.thirdcodes,
      limits: this.detail.limits,
    };

    const res = await this.saveUpdateSubclient(updateSubclient);

    if (res) {
      this.makeToast("success", this.$t(res.message) as string);
    }
  }

  async mounted(): Promise<void> {
    await this.initializeFormData();
  }

  formatDelegations(delegations: Delegation[]): void {
    delegations.forEach((delegation) => {
      if (delegation?.idSmartcash) {
        if (!this.optionsDelegations?.includes(delegation?.idSmartcash)) {
          this.optionsDelegations?.push(delegation?.idSmartcash + " - " + delegation?.description);
        } else {
          this.optionsDelegations.shift();
          this.optionsDelegations.push(delegation?.idSmartcash + " - " + delegation?.description);
          this.subclientDelegationSelected =
            delegation?.idSmartcash + " - " + delegation?.description;
        }
      }
    });
  }

  async synchronize(): Promise<void> {
    const centerCode = this.detail?.codeSol;
    const client = this.detail.client;
    const country = client?.country;
    if (!centerCode && !client && !country) {
      this.makeToastError();
      return;
    }
    const res = await this.$services.subclient.syncSubclient(this.detail.id!);

    if (!res) {
      this.makeToastError();
      return;
    }

    if (!res.success) {
      this.makeToastErrorWithDescription(res?.message || "unexpectedError.description");
      if (this.detail.active) {
        await this.showModalErrorOnSync();
      }
      return;
    }

    await this.synchronizedSubclient(res.data!, country!.id);

    this.$emit("fetch-subclient-required");

    this.makeToast("success", this.$t("validation.messages.subclient.updated").toString());
  }

  async synchronizedSubclient(
    syncedSubclient: ISubclientDetailData,
    countryId: number
  ): Promise<void> {
    this.descriptionInput = syncedSubclient.description;
    this.oidSolInput = syncedSubclient.oidSol;
    this.codeSolInput = syncedSubclient.codeSol;
    this.thirdCodes = syncedSubclient.thirdcodes || [];

    const delegations = await this.fetchDelegationsByCountry(countryId);
    if (delegations) {
      this.formatDelegations(delegations);
    }
  }

  async showModalErrorOnSync(): Promise<void> {
    const title = this.$t("validation.messages.cannot.synchronize.subclient.disclaimer.title");
    const message = this.$t("validation.messages.cannot.synchronize.subclient.disclaimer");
    const swal = await this.$swal({
      title: title,
      text: message,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes"),
      cancelButtonText: this.$t("label.no"),
    });
    if (swal.isConfirmed) {
      await this.updateStatusSubclient(this.detail.id!);
    }
    this.$emit("fetch-subclient-required");
  }

  makeToastErrorWithDescription(messageError: string): void {
    this.makeToast("danger", this.$t(messageError));
  }

  makeToastError(): void {
    this.makeToast("danger", this.$t("unexpectedError.description").toString());
  }

  public async initializeFormData(): Promise<void> {
    this.oidSolInput = this.detail?.oidSol;
    this.codeSolInput = this.detail?.codeSol;
    this.idSmartcashInput = this.detail?.idSmartcash;
    this.uniqueCodeInput = this.detail?.uniqueCode;
    this.descriptionInput = this.detail?.description;
    this.delegationInput = this.detail?.delegation?.idSmartcash;
    this.optionsDelegations?.push(this.detail?.delegation?.idSmartcash as string);
    this.subclientDelegationSelected = null;
    this.thirdCodes = this.detail?.thirdcodes || [];

    const rest = await this.fetchDelegationsByCountry(this.detail?.client?.country?.id);

    if (rest) {
      this.formatDelegations(rest);
    } else {
      this.optionsDelegations = [];
    }
  }
}
